import { createMiddleware } from 'hono/factory'
import type { NexusContext, NexusEnv, NexusMiddleware } from '../types.js'

export interface RateLimitOptions {
  /** Time window in milliseconds (default: 60000 = 1 minute) */
  windowMs?: number
  /** Max requests per window (default: 100) */
  max?: number
  /** Message when limit is reached */
  message?: string
  /** Custom key generator (default: IP-based) */
  keyGenerator?: (c: NexusContext) => string
  /** Don't count successful requests (2xx) toward the limit */
  skipSuccessfulRequests?: boolean
}

interface Bucket {
  count: number
  resetAt: number
}

/**
 * Per-process sliding-window rate limiter. Sufficient for F2 / single-instance
 * deployments. When multi-instance is required, swap to a shared-store
 * implementation (Redis INCR with PEXPIRE).
 *
 * LIMITATION: ManagedCache has no atomic incr/expire; using local Map fallback.
 */
function defaultKey(c: NexusContext): string {
  const fwd = c.req.header('x-forwarded-for')
  if (fwd) return fwd.split(',')[0]?.trim() ?? 'unknown'
  return c.req.header('x-real-ip') ?? 'unknown'
}

const noop: NexusMiddleware = createMiddleware<NexusEnv>(async (_c, next) => {
  await next()
})

export function createRateLimit(options: RateLimitOptions = {}): NexusMiddleware {
  if (process.env['NODE_ENV'] === 'test') return noop

  const {
    windowMs = 60 * 1000,
    max = 100,
    message = 'Too many requests, please try again later',
    keyGenerator = defaultKey,
    skipSuccessfulRequests = false
  } = options

  const buckets = new Map<string, Bucket>()

  // Opportunistic cleanup: sweep expired buckets on each hit if Map grows.
  const sweep = (now: number) => {
    if (buckets.size < 1024) return
    for (const [k, b] of buckets) {
      if (b.resetAt <= now) buckets.delete(k)
    }
  }

  return createMiddleware<NexusEnv>(async (c, next) => {
    const key = keyGenerator(c)
    const now = Date.now()
    sweep(now)

    let bucket = buckets.get(key)
    if (!bucket || bucket.resetAt <= now) {
      bucket = { count: 0, resetAt: now + windowMs }
      buckets.set(key, bucket)
    }

    const remainingBefore = Math.max(0, max - bucket.count)
    const resetSecs = Math.max(0, Math.ceil((bucket.resetAt - now) / 1000))

    // Draft-7 headers
    c.header('RateLimit-Policy', `${max};w=${Math.ceil(windowMs / 1000)}`)
    c.header('RateLimit-Limit', String(max))

    if (bucket.count >= max) {
      c.header('RateLimit-Remaining', '0')
      c.header('RateLimit-Reset', String(resetSecs))
      c.header('Retry-After', String(resetSecs))
      return c.json(
        { error: { code: 'RATE_LIMIT_EXCEEDED', message } },
        429
      )
    }

    bucket.count += 1
    c.header('RateLimit-Remaining', String(remainingBefore - 1))
    c.header('RateLimit-Reset', String(resetSecs))

    await next()

    if (skipSuccessfulRequests && c.res.status >= 200 && c.res.status < 300) {
      bucket.count = Math.max(0, bucket.count - 1)
    }
  })
}
